import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import './Auth.css';

const RegisterModal = ({ onClose, onSwitchToLogin }) => {
  const [step, setStep] = useState(1); // 1: form, 2: verification
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone_number: '',
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [callingEnabled, setCallingEnabled] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    authService.getConfig()
      .then((config) => setCallingEnabled(Boolean(config.calling_enabled)))
      .catch(() => setCallingEnabled(false));
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target.className === 'auth-modal') {
      onClose();
    }
  };

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (callingEnabled && !/^\+\d+$/.test(formData.phone_number)) {
      setError('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);
    try {
      if (callingEnabled) {
        await authService.sendVerificationCode(formData.phone_number);
        setStep(2);
        startCountdown();
      } else {
        const userData = await register({
          name: formData.name,
          email: formData.email,
          password: formData.password,
        });
        if (userData) {
          sessionStorage.setItem('showWelcomeConfetti', 'true');
          onClose();
          navigate('/');
        }
      }
    } catch (err) {
      const errorMessage = err.message;
      if (errorMessage.includes('Phone number already registered')) {
        setError('This phone number is already registered. Please use a different number or login.');
      } else if (errorMessage.includes('Email already registered')) {
        setError('This email address is already in use. Please use a different email or login.');
      } else {
        setError(errorMessage || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setError('');
    setIsLoading(true);
    try {
      await authService.sendVerificationCode(formData.phone_number);
      startCountdown();
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    setIsLoading(true);
    try {
      const userData = await register({
        ...formData,
        verification_code: verificationCode
      });
      if (userData) {
        sessionStorage.setItem('showWelcomeConfetti', 'true');
        onClose();
        navigate('/');
      }
    } catch (err) {
      const errorMessage = err.message;
      if (errorMessage.includes('Invalid verification code')) {
        setError('Invalid verification code. Please check and try again.');
      } else if (errorMessage.includes('Email already registered')) {
        setError('This email address is already in use. Please use a different email or login.');
        setStep(1);
      } else {
        setError(errorMessage || 'Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePhoneChange = (value, country) => {
    setFormData({
      ...formData,
      phone_number: `+${value}`
    });
  };

  const handleBack = () => {
    setStep(1);
    setVerificationCode('');
    setError('');
  };

  return (
    <div className="auth-modal" onClick={handleBackdropClick}>
      <div className="auth-modal-content">
        <button 
          className="close-button" 
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        {step === 1 ? (
          <>
            <h2>Unlimited Free AI Tarot Chat</h2>
            <p className="auth-subtitle">
              Create an account to keep asking questions, save your answers, and review each reading.
            </p>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleSendVerification}>
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                />
              </div>
              {callingEnabled && (
                <div className="form-group phone-input-group">
                  <div className="phone-label">📞 Phone Number</div>
                  <PhoneInput
                    country={'us'}
                    value={formData.phone_number.replace('+', '')}
                    onChange={handlePhoneChange}
                    disabled={isLoading}
                    enableSearch={true}
                    disableSearchIcon={true}
                    disableDropdown={false}
                    searchPlaceholder="Search countries..."
                    inputProps={{
                      required: true,
                      name: 'phone_number',
                      tabIndex: "0"
                    }}
                  />
                  <p className="phone-hint">We'll send a verification code to this number</p>
                </div>
              )}
              <div className="form-group">
                <input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  disabled={isLoading}
                />
              </div>
              <div className="form-group checkbox-group">
                <label className={`checkbox-label ${!agreedToTerms && error?.includes('Terms') ? 'highlight-error' : ''}`}>
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    disabled={isLoading}
                  />
                  <span>
                    I agree to the{' '}
                    <a href="https://nowtarot.com/terms/" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="https://nowtarot.com/privacy/" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
              <button type="submit" className="auth-button" disabled={isLoading}>
                {isLoading ? (callingEnabled ? 'Sending Code...' : 'Creating Account...') : (callingEnabled ? 'Continue' : 'Create Account')}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2>Verify Your Phone</h2>
            <p className="verification-info">
              We sent a 6-digit code to<br />
              <strong>{formData.phone_number}</strong>
            </p>
            {error && <div className="auth-error">{error}</div>}
            <form onSubmit={handleVerifyAndRegister}>
              <div className="form-group">
                <input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  disabled={isLoading}
                  className="verification-input"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
              <button type="submit" className="auth-button" disabled={isLoading || verificationCode.length !== 6}>
                {isLoading ? 'Verifying...' : 'Complete Sign Up'}
              </button>
            </form>
            <div className="verification-actions">
              <button 
                className="link-button" 
                onClick={handleResendCode}
                disabled={countdown > 0 || isLoading}
              >
                {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
              </button>
              <button className="link-button" onClick={handleBack} disabled={isLoading}>
                Change phone number
              </button>
            </div>
          </>
        )}

        <p>
          Already have an account?{' '}
          <button className="link-button" onClick={onSwitchToLogin}>
            Login
          </button>
        </p>
      </div>
    </div>
  );
};

export default RegisterModal;
